import _ from 'lodash';
import ko from '@tko/build.knockout';
import moment from 'moment-timezone';
import DataProvider from '../app/data-framework/data-provider';
import WebResourceSecure from '../modules/assurance/services/web-resource-secure.service';
import UtilsService from './utils.service';
import DefaultPreferenceService from './default-preference.service';

function buildObjectViews(appId, viewId, value) {
    return {
        'apps': {
          [appId]: {
              'views': {
                [viewId]: value
              }
          }
        }
    };
}

function buildObjectTab(appId, viewId, tabId, value) {
    var val = buildObjectViews(appId, viewId, {
        'tabs': {}
    });
    val.apps[appId].views[viewId].tabs[tabId] = value;
    return val;
}

function buildTab(appId, viewId, tabId, tabData) {
  tabData = tabData || {};
  return buildObjectViews(appId, viewId, {
      'tabs': {
        [tabId]: tabData
      }
  });
}

function buildObjectTabPanels(appId, viewId, tabId, value) {
    return buildTab(appId, viewId, tabId, {
        'panels': value,
    });
}

function buildObjectTabFixedPanel(appId, viewId, tabId, panelId, value) {
    return buildTab(appId, viewId, tabId, {
        'fixedpanels': {
          [panelId]: value
        },
    });
}

function buildObjectTabPanel(appId, viewId, tabId, panelId, value) {
    return buildObjectTabPanels(appId, viewId, tabId, {
      [panelId]: value
    });
}

function buildObjectTabPanelsFromValues(appId, viewId, tabId, values) {
    var val = buildObjectTabPanels(appId, viewId, tabId, {});
    _.forEach(values,  function (value) {
        if (_.isNil(value.id)) {
            throw new Error('preference.service::buildObjectTabPanelsFromValues - ' +
                'Mandatory parameter (id) is missing or empty in ' + value);
        }

        val.apps[appId].views[viewId].tabs[tabId].panels[value.id] = ko.toJS(value);
    })
    return val;
}

function buildObjectTabName(appId, viewId, tabId, name) {
    return buildObjectViews(appId, viewId, {
        'tabs': {
          [tabId]: { 'name' : name }
        }
    });
}

function addDefaultDatasetViewPreferences(self, appId, viewId) {
    return self.saveDatasetViewPreferences(appId, viewId,
        DefaultPreferenceService.getDefaultViewPreferences(viewId));
}

function getTabsFunc(appId, viewId, self, userId) {
    return function (baseUrl) {
        return WebResourceSecure.getJSON(baseUrl + '/apps/' + appId + '/views/' + viewId + '/tabs')
        .then(function (results) {
            if ('dashboard' === viewId && (_.isNil(results) || _.isEmpty(results))) {
                return self.addTab(appId, viewId, 'home', {id: 'home', name: 'Home'})
                .then(function (results) {
                    // todo check results
                    return self.getViewPreferences(appId, viewId);
                });
            } else if ((_.isNil(results) || _.isEmpty(results))
                && DefaultPreferenceService.getDefaultViewPreferences(viewId)) {
                // todo only if userId == current user
                return addDefaultDatasetViewPreferences(self, appId, viewId).then(
                    function (results) {
                        // todo check results
                        return self.getTabs(appId, viewId, userId);
                    });

            }
            return results;
        });
    };
}

/**
 * Gets the tab ids for the given view id.
 * @param {string} appId - the id of the app being viewed
 * @param {string} viewId - the id of the sub-nav being viewed
 * @return {Array.<Object>} an array of objects in the form {name: <tab_name>, id: <tab_id>}
 */
function getTabs(appId, viewId, userId) {
    var self = this;
    if (_.isNil(appId) || _.isNil(viewId)) {
        throw new Error('preference.service::getViewPreferences - ' +
            'Mandatory parameter (appId, viewId) is missing or empty.');
    }

    return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndUser(appId, userId)
        .then(getTabsFunc(appId, viewId, self, userId));
}

function getTabsForOrg(appId, viewId, orgId, userId) {
    var self = this;
    if (_.isNil(appId) || _.isNil(viewId)) {
        throw new Error('preference.service::getViewPreferences - ' +
            'Mandatory parameter (appId, viewId) is missing or empty.');
    }

    return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndOrgAndUser(appId, orgId, userId)
        .then(getTabsFunc(appId, viewId, self, userId));
}

function hasNoSaveURLParam() {
    return /[?&]nosave=true/.test(window.location.href);
}

export default {
    fetchPreferences: function (appId, filter) {
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, filter, 'POST');
            });
    },

    patchPreferences: function (appId, value) {
        if (hasNoSaveURLParam()) {
            return Promise.resolve(self.preferences);
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, value, 'PATCH');
            });
    },

    clearPreferences: function (appId) {
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, undefined, 'DELETE');
            });
    },

    deletePreferences: function (appId, value) {
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, value, 'DELETE');
            });
    },

    getDatasetViewPreferences: function (appId, viewId, datasetMetadata) {
        const self = this;
        if (_.isNil(appId) || _.isNil(viewId)) {
            throw new Error('preference.service::getDatasetViewPreferences - ' +
                'Mandatory parameter (appId, viewId) is missing or empty.');
        }

        return this.fetchPreferences(appId, buildObjectViews(appId, viewId, true)).then(function (results) {
            let datasetViewPreferences = _.get(results, 'apps.'+appId+'.views.'+viewId);
            if (datasetMetadata && !datasetViewPreferences) {
                let hiddenColumns = [];
                let columns = datasetMetadata.columns;
                _.forEach(columns, col => {
                    if (col['notShown']) {
                        hiddenColumns.push(col.id);
                    }
                });
                datasetViewPreferences = { gridConfig: { hiddenColumns: hiddenColumns, columnRevision: 0 } };
                // no preferences so save default
                self.saveDatasetViewPreferences(appId, viewId, datasetViewPreferences);
            }
            return datasetViewPreferences;
        });
    },

    saveDatasetViewPreferences: function (appId, viewId, value) {
        if (_.isNil(appId) || _.isNil(viewId)) {
            throw new Error('preference.service::saveDatasetViewPreferences - ' +
                'Mandatory parameter (appId, viewId) is missing or empty.');
        }

        return this.patchPreferences(appId, buildObjectViews(appId, viewId, ko.toJS(value)));
    },

    // get the panels for a tab
    getTabPreferences: function (appId, viewId, tabId) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::getTabPreferences - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }

        return this.fetchPreferences(appId, buildObjectTabPanels(appId, viewId, tabId, true)).then(function (results) {
            let panels = _.get(results, 'apps.'+appId+'.views.'+viewId+'.tabs.'+tabId+'.panels', {});
            // cleanup bad prefs UI-1601 && UI-2507 - let users deleted the phantom panel
            let badPanels = [];
            _.forEach(_.keys(panels), id => {
                if (!_.get(panels[id], 'id')) {
                    let currentPanel = panels[id];
                    badPanels.push(id);
                    currentPanel.id = id;
                    if (currentPanel.params && currentPanel.params.options) {
                      currentPanel.params.options.id = id;
                    }
                }
            });

            return _.values(panels);
        });
    },

    // get the panels for a tab for a specific user
    getUserTabPanels: function(userId, appId, viewId, tabId) {
      if (_.isNil(userId) || _.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
        throw new Error('preference.service::getUserTabPanels - ' +
          'Mandatory parameter (userId, appId, viewId, tabId) is missing or empty.');
      }

      return this.getUserTabPreferences(userId, appId, viewId, tabId).then(results => {
        let panels = results.panels;
        // cleanup bad prefs UI-1601 - let users deleted the phantom panel
        let badPanels = [];
        _.forEach(_.keys(panels), id => {
          if (!_.get(panels[id], 'id')) {
            badPanels.push(id);
            panels[id].id = id;
          }
        });

        return _.values(panels);
      })
    },

    saveTabPreferences: function (appId, viewId, tabId, values) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::saveTabPreferences - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return this.patchPreferences(appId,
          buildObjectTabPanels(appId,
            viewId,
            tabId.toString(),
            ko.toJS(_.keyBy(values, 'id'))));
    },

    // get the optional data for a tab
    getTabOptions: function (appId, viewId, tabId) {
        var self = this;

        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::getTabOptions - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }

        return this.fetchPreferences(appId, buildTab(appId, viewId, tabId, {
          selectedDataFilter: true,
          selectedDateRangeValues: true,
          selectedUtilizationValues: true,
        })).then(function (results) {
            if (tabId === '0'
                && _.isNil(_.get(results, 'apps.' + appId + '.views.' + viewId + '.tabs.' + tabId))
                && DefaultPreferenceService.getDefaultViewPreferences(viewId)) {
                return addDefaultDatasetViewPreferences(self, appId, viewId).then(function (results) {
                    // todo check results
                    return self.getTabOptions(appId, viewId, tabId);
                });

            }
            return _.get(results, 'apps.'+appId+'.views.'+viewId+'.tabs.'+tabId, {});
        });
    },

    saveTabOptions: function (appId, viewId, tabId, values) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::getTabOptions - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return this.patchPreferences(appId,
          buildTab(appId,
            viewId,
            tabId.toString(),
            ko.toJS(values)));
    },

    // old name is getViewPreferences
    getViewPreferences: getTabs,
    getTabs: getTabs,
    getTabsForOrg: getTabsForOrg,

    // get full object for a tab
    getTab: function (appId, viewId, tabId) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::getTab - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }

        return this.fetchPreferences(appId, buildTab(appId, viewId, tabId, true)).then(function (results) {
            return _.get(results, 'apps.'+appId+'.views.'+viewId+'.tabs.'+tabId, {});
        });
    },

    getFixedPanels: function (appId, viewId, tabId) {
        let self = this;
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::getTab - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }

        return this.fetchPreferences(appId, buildTab(appId, viewId, tabId, {fixedpanels: true})).then(function (results) {
            let fetchedPreferences = _.get(results, 'apps.'+appId+'.views.'+viewId+'.tabs.'+tabId+'.fixedpanels', {});
            if (_.values(fetchedPreferences).length > 0) {
                return _.values(fetchedPreferences);
            }
            
            // back up to default prefs
            let viewPrefs = DefaultPreferenceService.getDefaultViewPreferences(viewId);
            if (viewPrefs && Object.keys(viewPrefs.tabs).length > 0) {
                return self.addTab(appId, viewId, tabId, viewPrefs.tabs[tabId]).then((response) => {
                    return _.values(viewPrefs.tabs[tabId].fixedpanels);
                });
            }
            return [];
        });
    },

    deleteTab: function (appId, viewId, tabId) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::deleteTab - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl + '/apps/'+appId+'/views/'+viewId+'/tabs/' + tabId, undefined, 'DELETE');
            });
    },

    addTab: function (appId, viewId, tabId, value) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::addTab - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl + '/apps/'+appId+'/views/'+viewId+'/tabs/' + tabId, value, 'PUT');
            });
    },

    renameTab: function (appId, viewId, tabId, name) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
            throw new Error('preference.service::deleteTab - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, buildObjectTabName(appId, viewId, tabId, name), 'PATCH');
            });
    },

    deletePanel: function (appId, viewId, tabId, panelId) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId) || _.isNil(panelId)) {
            throw new Error('preference.service::deletePanel - ' +
                'Mandatory parameter (appId, viewId, tabId, panelId) is missing or empty.');
        }
        // todo replace with call to deletePreferences
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, buildObjectTabPanel(appId, viewId, tabId, panelId, true), 'DELETE');
            });
    },

    addOrUpdatePanel: function (appId, viewId, tabId, panelId, value) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId) || _.isNil(panelId)) {
            throw new Error('preference.service::addOrUpdatePanel - ' +
                'Mandatory parameter (appId, viewId, tabId, panelId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, buildObjectTabPanel(appId, viewId, tabId, panelId, ko.toJS(value)), 'PATCH');
            })
            .then(function (result) {
                return result; // for debugging
            });
    },

    addOrUpdatePanelTitle: function (appId, viewId, tabId, panelId, newTitle) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId) || _.isNil(panelId)) {
            throw new Error('preference.service::addOrUpdatePanelTitle - ' +
                'Mandatory parameter (appId, viewId, tabId, panelId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(
                                                 baseUrl,
                                                 buildObjectTabPanel(
                                                   appId,
                                                   viewId,
                                                   tabId,
                                                   panelId,
                                                   {params: {options: {panelHeader: newTitle}}}),
                                                 'PATCH');
            })
            .then(function (result) {
                return result; // for debugging
            });
    },

    addOrUpdatePanels: function (appId, viewId, tabId, values) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId) ) {
            throw new Error('preference.service::addOrUpdatePanel - ' +
                'Mandatory parameter (appId, viewId, tabId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, buildObjectTabPanelsFromValues(appId, viewId, tabId, values), 'PATCH');
            });
    },

    addOrUpdateUserPanels: function(userId, appId, viewId, tabId, values) {
      if (_.isNil(userId) || _.isNil(appId) || _.isNil(viewId) || _.isNil(tabId)) {
        throw new Error('preference.service::addOrUpdatePanel - ' +
          'Mandatory parameter (userId, appId, viewId, tabId) is missing or empty.');
      }
      return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndUser(appId, userId)
        .then(function(baseUrl) {
          return WebResourceSecure.getJSON(baseUrl, buildObjectTabPanelsFromValues(appId, viewId, tabId, values), 'PATCH');
        });
    },

    updateFixedPanel: function (appId, viewId, tabId, panelId, value) {
        if (_.isNil(appId) || _.isNil(viewId) || _.isNil(tabId) || _.isNil(panelId)) {
            throw new Error('preference.service::updateFixedPanel - ' +
                'Mandatory parameter (appId, viewId, tabId, panelId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferenceServiceBaseURL(appId)
            .then(function (baseUrl) {
                return WebResourceSecure.getJSON(baseUrl, buildObjectTabFixedPanel(appId, viewId, tabId, panelId, ko.toJS(value)), 'PATCH');
            })
            .then(function (result) {
                return result; // for debugging
            });
    },

    formatDateRange: function(dateRange) {
      var formattedDateRange = _.mapValues(dateRange, function(momentDate) {
          return momentDate.format('YYYY-MM-DD');
      });
      return formattedDateRange;
    },

    getDateRange: function (appId) {
      var defaultEnd = moment().endOf('month');
      var defaultStart = moment().startOf('month');

      return this.fetchPreferences(appId, {
          'apps': {
            [appId]: {
                selectedDateRangeValues: true
            }
          }
      }).then((result) => {
        let selectedDateRangeValues = _.get(result, 'apps.'+appId+'.selectedDateRangeValues', {});
        if (selectedDateRangeValues.startDate) {
          return selectedDateRangeValues;
        } else {
          return {
            startDate: defaultStart,
            endDate: defaultEnd
          };
        }
      });
    },
    saveDateRange: function (appId, dateRange) {
      if (!dateRange.startDate || !dateRange.endDate) {
        // dates not initialized
        return;
      }
      let formattedDateRange = {
        startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(dateRange.endDate).format('YYYY-MM-DD')
      };
      return this.patchPreferences(appId, {
          'apps': {
            [appId]: {
                selectedDateRangeValues: formattedDateRange
            }
          }
      });
    },

    clearPreferencesAdv: function (from) {
        let self = this;
        if (_.isEmpty(from.appIds) || !_.isArray(from.appIds)
            || !_.isNumber(from.orgId) || _.isEmpty(from.users)
            || !_.isArray(from.users)) {
            throw new Error('preference.service::clearPreferencesAdv - ' +
                'Mandatory parameter (from) is missing or empty or wrong type.');
        }
        console.log("preference.service.clear preferences: " + JSON.stringify(from));

        let calls = [];
        _.forEach(from.users, function(fromUser){
            _.forEach(from.appIds, function(fromAppId){
                calls.push(
                    DataProvider.getInstance().fetchPreferenceServiceBaseURLForUser(from.orgId, fromUser, fromAppId)
                    .then(function (baseUrl) {
                        // console.log('DELETE', baseUrl);
                        return WebResourceSecure.getJSON(baseUrl, undefined, 'DELETE');
                    }));
            });
        });
        return Promise.all(calls);
    },


    addTabs: function (from, to, replace) {
        var self = this;
        if (_.isEmpty(from.appId) || _.isEmpty(from.viewId) || !_.isNumber(from.user)
            || _.isEmpty(from.boards) || !_.isArray(from.boards)
            || _.isEmpty(to.appId) || _.isEmpty(to.viewId) || _.isEmpty(to.users)
            || !_.isArray(to.users)) {
            throw new Error('preference.service::addTabs - ' +
                'Mandatory parameter (from, to) is missing or empty or wrong type.');
        }
        console.log("preference.service.addTabs view: " + to.viewId);

        _.forEach(to.users, function(toUser){
            self.addTabs0(from, to, toUser, _.clone(from.boards), replace);
        });
    },

    addTabs0: function (from, to, toUser, boards, replace) {
        var self = this;
        if (_.isEmpty(boards) || _.isEmpty(from.appId) || _.isEmpty(from.viewId) || !_.isNumber(from.user)
            || _.isEmpty(to.appId) || _.isEmpty(to.viewId)) {
            return;
        }

        var tabId = boards.shift();
        return self.getUserTabPreferences(from.user, from.appId, from.viewId, tabId).then(function (tabValue) {
            if (_.isEmpty(tabValue)) {
                throw new Error('preference.service::addTabs0 - ' +
                    'Unable to load from board app: ' + from.appId + " view: " + from.viewId + " tabId: " + tabId + " - board does not exist");
            }
            var newTabId = UtilsService.generateID();
            if (!replace) {
                tabValue.id = newTabId;
            }

            return self.getUserTabsPreferences(toUser, to.appId, to.viewId).then(function() { // this is to ensure the user had preferences to edit - get defaults/migrate if need be before saving changes
                return self.addUserTab(toUser, to.appId, to.viewId, newTabId, tabValue).then(function() {
                    return self.addTabs0(from, to, toUser, boards, replace)
                });
            })

        });

    },

    fetchUserPreferences: function (appId, userId, filter) {
        if (!userId || _.isEmpty(filter)) {
            throw new Error('preference.service::fetchUserPreferences - ' +
                'Mandatory parameter (userId, filter) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndUser(appId, userId)
        .then(function (baseUrl) {
            return WebResourceSecure.getJSON(baseUrl, filter, 'POST');
        });
    },

    getUserTabPreferences: function (userId, appId, viewId, tabId) {
        var self = this;
        if (!userId || _.isEmpty(appId) || _.isEmpty(viewId) || _.isEmpty(tabId)) {
            throw new Error('preference.service::getUserTabPreferences - ' +
                'Mandatory parameter (userId, appId, viewId, tabId) is missing or empty.');
        }

        return self.fetchUserPreferences(appId, userId, buildObjectTab(appId, viewId, tabId, true)).then(function (results) {
            return _.get(results, 'apps.'+appId+'.views.'+viewId+'.tabs.'+tabId, {});
        });
    },

    getUserTabsPreferences: function (userId, appId, viewId) {
        if (!userId || _.isEmpty(appId) || _.isEmpty(viewId)) {
            throw new Error('preference.service::getUserTabsPreferences - ' +
                'Mandatory parameter (userId, appId, viewId) is missing or empty.');
        }

        return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndUser(appId, userId)
        .then(function (baseUrl) {
            return WebResourceSecure.getJSON(baseUrl + '/apps/'+appId+'/views/'+viewId+'/tabs').then(function (results) {
                return results;
            });
        });
    },

    addUserTab: function (userId, appId, viewId, tabId, value) {
        if (!userId || _.isEmpty(appId) || _.isEmpty(viewId) || _.isEmpty(tabId)) {
            throw new Error('preference.service::addUserTab - ' +
                'Mandatory parameter (userId, appId, viewId, tabId) is missing or empty.');
        }
        return DataProvider.getInstance().fetchPreferencesServicePreferencesBaseURLForAppAndUser(appId, userId)
        .then(function (baseUrl) {
            return WebResourceSecure.getJSON(baseUrl + '/apps/'+appId+'/views/'+viewId+'/tabs/' + tabId, value, 'PUT');
        });
    }
};
